/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import ApiService from "./ApiService";
import Attributes from "model/stats/Attributes";
import AttributeValues from "model/stats/AttributeValues";
import CharacterService from "./CharacterService";
import Section from "model/abilities/Section";
import Class from "model/character/Class";
import { ClassDetails } from "model/character/ClassDetails";
import { fixClassErrors } from "model/formats/KnownErrors";
import Abilities from "model/abilities/Abilities";
import * as ClassMetadata from "./ClassMetadata";
import { parseSkills } from "./SkillParsingService";

interface Option {
    label: string,
    options: string[]
};

interface ClassOpen5e extends Class {
    archetypes: Class[];
}

class ClassService {
    private static baseMetaCaster = {
        name: 'Meta Caster',
        desc: '',
        slug: 'meta_caster',
        base: { name: '' },
        subtypes_name: '',
        hit_dice: '',
        prof_armor: '',
        prof_weapons: '',
        prof_tools: '',
        prof_saving_throws: '',
        prof_skills: '',
        equipment: '',
        table: '',
        sections: [],
        getAsiLevels: (level: number) => [],
        document__slug: 'meta_caster',
        document__title: '',
        document__license_url: '',
        document__url: ''
    };

    // Single meta-caster — the spellcasting attribute is chosen via the attribute selector
    // in the spellbook modal rather than being baked into the class name.
    static metaCaster = {
        ...ClassService.baseMetaCaster,
        name: 'Inate Caster',
        slug: 'inate_caster',
        spellcasting_ability: ''
    };

    static expandArchetypes(classes: ClassOpen5e[]): Class[] {
        return classes.reduce((acc: Class[], pcClass: ClassOpen5e) => {
            const { archetypes, ...parentAttributes } = pcClass;
            acc.push(parentAttributes);
            archetypes.forEach(archetype => {
                // Deep clone parent attributes to avoid mutations
                const parentClone = JSON.parse(JSON.stringify(parentAttributes));

                // Function to combine strings with a check to avoid duplicating identical content
                const describeArchetype = (name: string, desc: string) => {
                    return `### ${name}\n${desc}`;
                };

                const newArchetype: Class = {
                    ...parentClone,            // Start with a clone of the parent attributes
                    ...archetype,              // Overwrite with archetype-specific attributes
                    base: parentClone,
                    archetypeName: archetype.name,
                    slug: archetype.slug,
                    desc: describeArchetype(archetype.name, archetype.desc)
                };

                acc.push(newArchetype);
            });
            return acc;
        }, []);
    }
    static getLevelString(levelInt: number): string {
        if (levelInt == 1) return '1st';
        if (levelInt == 2) return '2nd';
        if (levelInt == 3) return '3rd';
        return levelInt + 'th';
    }
    static getLevelAbility(classInfo: Class, classLevel: number, ability: string): string {
        if (classInfo) {
            if (classLevel > 20) {
                classLevel = 20;
            }
            const levelStr = this.getLevelString(classLevel);
            let tableRow = CharacterService.getTableRow(classInfo.table, levelStr);
            for (const tableCell of tableRow || []) {
                if (tableCell.name.toLowerCase() === ability.toLowerCase()) {
                    return tableCell.desc;
                }
            }
            for (const section of classInfo.sections || []) {
                if (section.desc.includes('--|--')) {
                    tableRow = CharacterService.getTableRow(section.desc, levelStr);
                    if (tableRow) {
                        break;
                    }
                }
            }
            for (const tableCell of tableRow || []) {
                if (tableCell.name.toLowerCase() === ability.toLowerCase()) {
                    return tableCell.desc;
                }
            }
        }
        return '';
    }

    static getSpellCastingAttribute(classDetails: ClassDetails): string {
        const fullClassName = (classDetails.classInfo?.name || '') + '/' + (classDetails.archetype?.name || '');
        return ClassService.getSpellCastingAttributeByClassName(fullClassName) ||
            ClassService.getSpellCastingAttributeByClassName(classDetails.classInfo?.name || '');

    }
    static getSpellCastingAttributeByClassName(fullClassName: string): string {
        return ClassMetadata.getSpellCastingAttributeByClassName(fullClassName);
    }

    static getSpellsKnownForClassLevel(classInfo: Class | null, classLevel: number, attributeValues: AttributeValues): number {
        if (classInfo == null) {
            return 0;
        }
        if (classInfo.name.toLowerCase().startsWith('inate caster') || classLevel > 20) {
            return 99;
        }
        if (classInfo.name.toLowerCase() === 'wizard') {
            // This is really the count of prepared spells.
            return Math.max(1, (classLevel + Attributes.getBonus(attributeValues.intelligence)));
        }
        if ((classInfo.name.toLowerCase() === 'cleric') ||
            (classInfo.name.toLowerCase() === 'druid')) {
            return Math.max(1, (classLevel + Attributes.getBonus(attributeValues.wisdom)));
        }
        if (classInfo.name.toLowerCase() === 'paladin') {
            return Math.max(1, (Math.floor(classLevel / 2) + Attributes.getBonus(attributeValues.charisma)));
        }
        if ((classInfo.name.toLowerCase() === 'smuggler') && (classLevel >= 7)) {
            if (classLevel >= 13) {
                return 2;
            }
            return 1;
        }
        const spellsKnownAtLevel = this.getLevelAbility(classInfo, classLevel, 'Spells Known');
        return parseInt(spellsKnownAtLevel) || 0;
    }

    static getHighestSpellsLevelKnownForClassLevel(classInfo: Class | null, classLevel: number): number {
        if (!classInfo) {
            return 0;
        }
        if (classInfo.name.toLowerCase().startsWith('inate caster')) {
            return 9;
        }
        if (classInfo.name.toLowerCase() === 'warlock') {
            const spellsKnownAtSpellLevel = this.getLevelAbility(classInfo, classLevel, 'Slot Level');
            if (spellsKnownAtSpellLevel) {
                return parseInt(spellsKnownAtSpellLevel);
            }
        }
        for (let spellLevel = 9; spellLevel > 0; spellLevel--) {
            const spellLevelStr = this.getLevelString(spellLevel);
            const spellsKnownAtSpellLevel = this.getLevelAbility(classInfo, classLevel, spellLevelStr);
            if (spellsKnownAtSpellLevel) {
                const spells = parseInt(spellsKnownAtSpellLevel);
                if (spells && spells > 0) {
                    return spellLevel;
                }
            }
        }
        // Fallback: if the class table lookup fails (e.g. column headers don't match '1st'/'2nd'/...),
        // use the well-known progression for full casters: a new spell level every 2 class levels,
        // starting at level 1 (so level 1→1st, 3→2nd, 5→3rd, ..., 17→9th).
        const fullCasters = ['bard', 'cleric', 'druid', 'sorcerer', 'wizard'];
        if (fullCasters.includes(classInfo.name.toLowerCase())) {
            return Math.min(9, Math.ceil(classLevel / 2));
        }
        // Half casters (Paladin, Ranger): new spell level at class levels 2, 5, 9, 13, 17.
        const halfCasters = ['paladin', 'ranger'];
        if (halfCasters.includes(classInfo.name.toLowerCase())) {
            if (classLevel < 2) return 0;
            return Math.min(5, Math.ceil(classLevel / 4));
        }
        return 0;
    }

    static getCantripsKnonwn(classInfo: Class, classLevel: number): number {
        if ((classInfo.name.toLowerCase() === 'smuggler') && (classLevel >= 3)) {
            return 2;
        }
        const cantripsKnown = this.getLevelAbility(classInfo, classLevel, 'Cantrips Known') ||
            this.getLevelAbility(classInfo, classLevel, 'Known Cantrips');
        return parseInt(cantripsKnown) || 0;
    }

    static getSpellsKnowableBySpellLevelForClassLevel(classInfo: Class, classLevel: number, attributeValues: AttributeValues): number[] {
        if (classLevel < 1) {
            return [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        }
        if (classInfo.name.toLowerCase().startsWith('inate caster') || classLevel > 20) {
            const maxLevel = ClassService.getHighestSpellsLevelKnownForClassLevel(classInfo, classLevel);
            const results = [];
            for (let spellLevel = 0; spellLevel <= maxLevel; spellLevel++) {
                results[spellLevel] = 9;
            }
            return results;
        }
        if (classInfo.name.toLowerCase() === 'smuggler') {
            if (classLevel >= 13) {
                return [2, 1, 1, 0, 0, 0, 0, 0, 0, 0];
            }
            if (classLevel >= 7) {
                return [2, 1, 0, 0, 0, 0, 0, 0, 0, 0];
            }
            if (classLevel >= 3) {
                return [2, 0, 0, 0, 0, 0, 0, 0, 0, 0];
            }
            return [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        }
        const cantripsKnown = this.getCantripsKnonwn(classInfo, classLevel);
        if (classInfo.name.toLowerCase() === 'wizard') {
            const spells = [];
            spells.push(cantripsKnown)
            for (let spellLevel = 1; spellLevel <= 9; spellLevel++) {
                if (spellLevel == this.getHighestSpellsLevelKnownForClassLevel(classInfo, classLevel)) {
                    // Prepared spells = INT mod + wizard level, minimum of 1 per the rules.
                    spells.push(Math.max(1, Attributes.getBonus(attributeValues.intelligence) + classLevel));
                } else {
                    spells.push(0);
                }
            }
            return spells;
        }
        if ((classInfo.name.toLowerCase() === 'cleric') ||
            (classInfo.name.toLowerCase() === 'druid') ||
            (classInfo.name.toLowerCase() === 'paladin')) {
            // clerics really know all the spells, but this list will show the prepared spells instead:
            const spells = this.getSpellsKnownForClassLevel(classInfo, classLevel, attributeValues);
            const spellsByLevel = [];
            spellsByLevel.push(cantripsKnown);
            const highestLevel = this.getHighestSpellsLevelKnownForClassLevel(classInfo, classLevel);
            for (let lvl = 1; lvl <= highestLevel; lvl++) {
                spellsByLevel.push(spells);
            }
            while (spellsByLevel.length < 10) {
                spellsByLevel.push(0);
            }
            return spellsByLevel;
        }
        if (classLevel == 1) {
            const spellsKnownBySpellLevel: number[] = [];
            spellsKnownBySpellLevel.push(cantripsKnown);
            spellsKnownBySpellLevel.push(this.getSpellsKnownForClassLevel(classInfo, classLevel, attributeValues));
            for (let spellLevel = 2; spellLevel <= 9; spellLevel++) {
                spellsKnownBySpellLevel.push(0);
            }
            return spellsKnownBySpellLevel;
        }
        const spellsKnownBySpellLevel = this.getSpellsKnowableBySpellLevelForClassLevel(classInfo, classLevel - 1, attributeValues);
        spellsKnownBySpellLevel[0] = cantripsKnown;
        const spellsKnown = this.getSpellsKnownForClassLevel(classInfo, classLevel, attributeValues);
        const spellsKnownPriorLevel = this.getSpellsKnownForClassLevel(classInfo, classLevel - 1, attributeValues);
        const newSpells = spellsKnown - spellsKnownPriorLevel;
        const highestSpellsLevelKnown = this.getHighestSpellsLevelKnownForClassLevel(classInfo, classLevel);
        spellsKnownBySpellLevel[highestSpellsLevelKnown] = spellsKnownBySpellLevel[highestSpellsLevelKnown] + newSpells;
        // remove one of the lowest level spells, and add it to the highest level

        for (let spellLevel = 1; spellLevel <= 9; spellLevel++) {
            if (spellsKnownBySpellLevel[spellLevel] > 0) {
                spellsKnownBySpellLevel[spellLevel] = spellsKnownBySpellLevel[spellLevel] - 1;
                spellsKnownBySpellLevel[highestSpellsLevelKnown] = spellsKnownBySpellLevel[highestSpellsLevelKnown] + 1;
                break;
            }
        }
        return spellsKnownBySpellLevel;
    }

    static findMinLevel(text: string): number | undefined {
        const parts = text.split(' level,');
        if (parts.length > 0) {
            const firstPart = parts[0];
            const startWords = ['At ', 'By ', 'Beginning at ', 'Starting at ', 'When you reach ', 'Upon reaching ', 'Also at ', 'Beginning when you reach '];
            for (const startWord of startWords) {
                if (firstPart.startsWith(startWord)) {
                    const numberPart = firstPart.substr(startWord.length).trim();
                    const result = numberPart.match(/\d+/);  // This regex matches the sequence of digits
                    return result ? parseInt(result[0], 10) : undefined; // Parse the match as an integer
                }
            }
        }

        const levelMatch = text.match(/^\*Prerequisite: (\d+)th level/);
        if (levelMatch) {
            return parseInt(levelMatch[1], 10);
        }

        return 0;
    }

    static checkAttributeRequirement(attributeName: string, attributeVals: AttributeValues): boolean {
        for (const attr of attributeName.split(' or ')) {
            const attrLevel = attributeVals[attr.toLowerCase() as keyof AttributeValues];
            if (attrLevel >= 13) {
                return true;
            }
        }
        return false;
    }

    static getSecondClassArmorProficiencies(classInfo: Class): string {
        return ClassMetadata.getSecondClassArmorProficiencies(classInfo);
    }

    static getSecondClassWeaponProficiencies(classInfo: Class): string {
        return ClassMetadata.getSecondClassWeaponProficiencies(classInfo);
    }

    static getSecondClassSkillProficiencies(classInfo: Class): string {
        return ClassMetadata.getSecondClassSkillProficiencies(classInfo);
    }

    static checkMultiClassRequirement(className: string, attributeVals: AttributeValues): Set<string> {
        const attrName = ClassMetadata.getMultiClassRequirementAttribute(className);
        const errors = new Set<string>();
        for (const attr of attrName.split(' and ')) {
            if (!ClassService.checkAttributeRequirement(attr, attributeVals)) {
                errors.add(attr);
            }
        }
        return errors;
    }

    static parseTextToSections(text: string): Section[] {
        const sectStrs: string[] = text.split(/\n *(?=(?:[#_\*]{2,}|> \#{3,}))/); // Split by lines starting with '##', '###', or '####', or '**', '***', or '> ###'...
        const sectsOrNulls: (Section | null)[] = sectStrs.map(sect => {
            const options: string[] = [];
            const selectedOption = '';
            const children: Section[] = [];

            let depth = 0;
            if (sect.startsWith('> ')) {
                sect = sect.substring(2).replace(/\n> /g, '\n');
                depth++;
            }
            if (sect.startsWith('##') || sect.includes('--|--')) {
                let [name, ...descriptionParts] = sect.trim().split('\n');
                const desc = descriptionParts.join('\n').trim(); // Join back the description parts split earlier
                name = name.trim();
                while (name.startsWith('#')) {
                    depth++;
                    name = name.substr(1);
                }
                while ((name.startsWith('*') && name.endsWith('*')) || (name.startsWith('_') && name.endsWith('_'))) {
                    name = name.substr(1, name.length - 2);
                }
                name = name.trim();

                return new Section(name.trim(), desc, depth, ClassService.findMinLevel(desc), children, null, options, selectedOption);
            }
            if (sect.startsWith('**')) {
                const array = sect.trim().split(/[\*_]+/);
                return new Section(
                    '',//array[1].trim(), // Name is taken directly with its hashes
                    '>' + sect, // array[2]
                    depth + 3,
                    ClassService.findMinLevel(array[2]),
                    children, null, options, selectedOption
                );
            }
            return null;
        });
        return sectsOrNulls.filter(o => o != null) as Section[];
    };

    static parseSections(pcClass: Class): void {
        if (!pcClass.desc) return;

        pcClass.desc = pcClass.desc.replace(/ \n \n/g, '\n');
        const sections = ClassService.parseTextToSections(pcClass.desc);
        pcClass.sections = [];
        let lastSection = null;
        for (const section of sections) {
            if (section.name === 'Your Pact Boon') {
                section.depth--;
            }
            if (lastSection && section.depth === lastSection.depth + 1) {
                lastSection.children.push(section);
                if ((lastSection.name === 'Fighting Style') ||
                    (lastSection.name === 'Pact Boon') ||
                    (lastSection.name === 'Eldritch Invocations') ||
                    (lastSection.name === 'Metamagic') ||
                    (lastSection.name === 'Wyrmling Blessing')) {
                    section.parentName = lastSection.name;
                    lastSection.options.push(section.name);
                }
            } else {
                pcClass.sections.push(section);
                lastSection = section;
            }
        }
    };

    static addAsiLevelToClass(pcClass: Class): void {
        if (!pcClass) return;
        pcClass.getAsiLevels = (level: number): number[] => {
            const rows = pcClass.table.split('\n');
            const headers = rows[0].split('|').map((o) => o.trim());
            const featureColumnIndex = headers.indexOf('Features');
            const asiLevels: number[] = [];
            for (let i = 0; (i < level) && (rows.length > (2 + i)); i++) {
                const featureAtLevel = rows[2 + i].split('|')[featureColumnIndex];
                if (featureAtLevel.includes('Ability Score Improvement')) {
                    asiLevels.push(i + 1);
                }
            }
            return asiLevels;
        }
    };

    static parseLanguages(classInfo: Class | null, languageOptions: Option[], languagesSelected: string[]): void {
        classInfo?.sections?.forEach(section => {
            if (section.name.toLowerCase() === 'thieves\' cant') {
                const lang = 'Thieves\' cant';
                languageOptions.push({ label: classInfo.name, options: [lang] });
                languagesSelected.push(lang);
            }
            if (section.name.toLowerCase() === 'druidic') {
                const lang = 'Druidic';
                languageOptions.push({ label: classInfo.name, options: [lang] });
                languagesSelected.push(lang);
            }
        });
    }

    static parseArmor(classInfo: Class | null, armorOptions: Option[], armorSelected: string[], isPrimaryClass: boolean): void {
        let prof_armor = classInfo?.prof_armor;
        if (!isPrimaryClass && classInfo) {
            prof_armor = this.getSecondClassArmorProficiencies(classInfo);
        }
        const isDruid = prof_armor?.includes('(druids will not wear armor or use shields made of metal)');
        if (isDruid) {
            prof_armor = prof_armor?.replace('(druids will not wear armor or use shields made of metal)', '').trim();
        }
        const armors = prof_armor?.toLocaleLowerCase().split(',').map(ar => ar.trim()) || [];
        for (const armor of ['Light Armor', 'Medium Armor', 'Heavy Armor', 'All Armor', 'Shields']) {
            if (armors.includes(armor.toLocaleLowerCase())) {
                if (isDruid) {
                    armorOptions.push({ label: classInfo?.name || 'Class', options: [armor + ' (not made of metal)'] });
                } else {
                    armorOptions.push({ label: classInfo?.name || 'Class', options: [armor] });
                }
                armorSelected.push(armor);
            }
        }
    }

    static parseWeapons(classInfo: Class | null, weaponsOptions: Option[], weaponsSelected: string[], isPrimaryClass: boolean): void {
        let prof_weapons = classInfo?.prof_weapons;
        if (!isPrimaryClass && classInfo) {
            prof_weapons = this.getSecondClassWeaponProficiencies(classInfo);
        }
        const weapons = prof_weapons?.toLocaleLowerCase().split(',').map(ar => ar.trim()) || [];
        for (const weapon of [...Abilities.getGearNames(), 'Simple Weapons', 'Martial Weapons']) {
            if (weapons.includes(weapon.toLocaleLowerCase()) || weapons.includes(weapon.toLocaleLowerCase() + 's')) {
                weaponsOptions.push({ label: classInfo?.name || 'Class', options: [weapon] });
                weaponsSelected.push(weapon);
            }
        }
    }

    static parseEquipment(classInfo: Class | null, equipmentOptions: Option[], equipmentSelected: string[], isPrimaryClass: boolean): void {
        if (isPrimaryClass && classInfo) {
            // secondary classes dont get equipment
            const eqOptions: string[] = classInfo.equipment.split(/[\n]\s?/);
            eqOptions.forEach(equipment => {
                let options: string[] = equipment.split(/\*?\(\*?[a-z]\*?\)\*?/i);
                options = options.map(o => {
                    o = o.trim();
                    if (o.startsWith('*')) {
                        o = o.substring(1).trim();
                    }
                    if (o.endsWith(' or')) {
                        o = o.substring(0, o.length - 3).trim();
                    }
                    if (o.endsWith(',')) {
                        o = o.substring(0, o.length - 1).trim();
                    }
                    if (o.toLocaleLowerCase() === 'you start with the following equipment, in addition to the equipment granted by your background:') {
                        return '';
                    }
                    return o;
                });
                // remove blank/empty rows:
                options = options.filter(o => !!o);
                if (options.length > 0) {
                    if (options.length === 1) {
                        const singleOption = options[0];
                        if (singleOption.includes(' and ')) {
                            const multiOptions = singleOption.replace(' and ', ',').replace(',,', ',').split(',');
                            multiOptions.forEach(opt => {
                                equipmentOptions.push({ label: classInfo.name, options: [opt] });
                                equipmentSelected.push(opt);
                            });
                        } else {
                            equipmentOptions.push({ label: classInfo.name, options: options });
                            equipmentSelected.push(singleOption);
                        }
                    } else {
                        equipmentOptions.push({ label: classInfo.name, options: options });
                        equipmentSelected.push('')
                    }
                }
            });
        }
        // •	(a) a greataxe or (b) any martial melee weapon
        // •	(a) two handaxes or (b) any simple weapon
        // •	An explorer's pack and four javelins
        // -----------
        // •	(a) a rapier, (b) a longsword, or (c) any simple weapon
        // •	(a) a diplomat's pack or (b) an entertainer's pack
        // •	(a) a lute or (b) any other musical instrument
        // •	Leather armor and a dagger
        // -----------
        // •	(a) a mace or (b) a warhammer (if proficient)
        // •	(a) scale mail, (b) leather armor, or (c) chain mail (if proficient)
        // •	(a) a light crossbow and 20 bolts or (b) any simple weapon
        // •	(a) a priest's pack or (b) an explorer's pack
        // •	A shield and a holy symbol
        // -----------
        // •	(a) a wooden shield or (b) any simple weapon
        // •	(a) a scimitar or (b) any simple melee weapon
        // •	Leather armor, an explorer's pack, and a druidic focus
        // -----------
        // •	(a) chain mail or (b) leather armor, longbow, and 20 arrows
        // •	(a) a martial weapon and a shield or (b) two martial weapons
        // •	(a) a light crossbow and 20 bolts or (b) two handaxes
        // •	(a) a dungeoneer's pack or (b) an explorer's pack
        // -----------
        // •	(a) a shortsword or (b) any simple weapon
        // •	(a) a dungeoneer's pack or (b) an explorer's pack
        // •	10 darts
        // -----------
        // •	(a) a martial weapon and a shield or (b) two martial weapons
        // •	(a) five javelins or (b) any simple melee weapon
        // •	(a) a priest's pack or (b) an explorer's pack
        // •	Chain mail and a holy symbol
        // -----------
        // •	(a) scale mail or (b) leather armor
        // •	(a) two shortswords or (b) two simple melee weapons
        // •	(a) a dungeoneer's pack or (b) an explorer's pack
        // •	A longbow and a quiver of 20 arrows
        // -----------
        // •	(a) a rapier or (b) a shortsword
        // •	(a) a shortbow and quiver of 20 arrows or (b) a shortsword
        // •	(a) a burglar's pack, (b) a dungeoneer's pack, or (c) an explorer's pack
        // •	(a) Leather armor, two daggers, and thieves' tools
        // -----------
        // •	(a) a light crossbow and 20 bolts or (b) any simple weapon
        // •	(a) a component pouch or (b) an arcane focus
        // •	(a) a dungeoneer's pack or (b) an explorer's pack
        // •	Two daggers
        // -----------
        // •	(a) a light crossbow and 20 bolts or (b) any simple weapon
        // •	(a) a component pouch or (b) an arcane focus
        // •	(a) a scholar's pack or (b) a dungeoneer's pack
        // •	Leather armor, any simple weapon, and two daggers
        // -----------
        // •	(a) a quarterstaff or (b) a dagger
        // •	(a) a component pouch or (b) an arcane focus
        // •	(a) a scholar's pack or (b) an explorer's pack
        // •	A spellbook



        // a greataxe or 
        // any martial melee weapon
        // two handaxes or 
        // any simple weapon
        // An explorer's pack and four javelins
        // -----------
        // a rapier, 
        // a longsword, or 
        // any simple weapon
        // a diplomat's pack or 
        // an entertainer's pack
        // a lute or 
        // any other musical instrument
        // Leather armor and a dagger
        // -----------
        // a mace or 
        // a warhammer (if proficient)
        // scale mail, 
        // leather armor, or 
        // chain mail (if proficient)
        // a light crossbow and 20 bolts or 
        // any simple weapon
        // a priest's pack or 
        // an explorer's pack
        // A shield and a holy symbol
        // -----------
        // a wooden shield or 
        // any simple weapon
        // a scimitar or 
        // any simple melee weapon
        // Leather armor, an explorer's pack, and a druidic focus
        // -----------
        // chain mail or 
        // leather armor, longbow, and 20 arrows
        // a martial weapon and a shield or 
        // two martial weapons
        // a light crossbow and 20 bolts or 
        // two handaxes
        // a dungeoneer's pack or 
        // an explorer's pack
        // -----------
        // a shortsword or 
        // any simple weapon
        // a dungeoneer's pack or 
        // an explorer's pack
        // 10 darts
        // -----------
        // a martial weapon and a shield or 
        // two martial weapons
        // five javelins or 
        // any simple melee weapon
        // a priest's pack or 
        // an explorer's pack
        // Chain mail and a holy symbol
        // -----------
        // scale mail or 
        // leather armor
        // two shortswords or 
        // two simple melee weapons
        // a dungeoneer's pack or 
        // an explorer's pack
        // A longbow and a quiver of 20 arrows
        // -----------
        // a rapier or 
        // a shortsword
        // a shortbow and quiver of 20 arrows or 
        // a shortsword
        // a burglar's pack, 
        // a dungeoneer's pack, or 
        // an explorer's pack
        // Leather armor, two daggers, and thieves' tools
        // -----------
        // a light crossbow and 20 bolts or 
        // any simple weapon
        // a component pouch or 
        // an arcane focus
        // a dungeoneer's pack or 
        // an explorer's pack
        // Two daggers
        // -----------
        // a light crossbow and 20 bolts or 
        // 
        // any simple weapon
        // a component pouch or 
        // 
        // an arcane focus
        // a scholar's pack or 
        // 
        // a dungeoneer's pack
        // Leather armor, any simple weapon, and two daggers
        // -----------
        // a quarterstaff or 
        // 
        // a dagger
        // a component pouch or 
        // 
        // an arcane focus
        // a scholar's pack or 
        // 
        // an explorer's pack
        // A spellbook

    }

    static parseOptions(label: string, input: string | undefined, options: Option[], selected: string[]): void {
        const optionsArray: string[][] = this.parseOptions2(input);
        for (const opts of optionsArray) {
            options.push({ label, options: opts })
            selected.push(opts[0]);
        }
    }

    static parseOptions2(input: string | undefined): string[][] {
        if (!input) {
            return [];
        }
        function cleanEnding(str: string) {
            return str
                .replace(/\s*(?:,\s*or|or|,)\s*$/i, '') // only at END of string
                .trim();
        }

        const result = input
            .split(/\r?\n/)                                   // split rows
            .map(line => line.replace(/^•\s*/, '').trim())    // strip bullet & spaces
            .filter(line => line.length)                      // drop empty lines
            .map(line => {
                const choices = line.match(/\([a-z]\)[^()]+/gi); // find "(a) ..." chunks
                if (choices) {
                    return choices.map(c => cleanEnding(c.replace(/\([a-z]\)/i, '').trim()));
                }
                return [cleanEnding(line)];
            });
        return result;
    }

    static parseSkills(classDetails: ClassDetails, skillOptions: Option[],
                       skillsSelected: string[], isPrimaryClass: boolean): void {
        parseSkills(classDetails, skillOptions, skillsSelected, isPrimaryClass);
    }

    static processClass(classDetails: ClassDetails | null, monsterSections: Section[]): void {
        if (classDetails?.classInfo) {
            const subtypes_name = classDetails.classInfo.subtypes_name;
            const sectionsByName = new Map<string, Section>();
            for (const section of classDetails.combinedSections) {
                sectionsByName.set(section.name, section);
            }
            for (const section of classDetails.combinedSections) {
                const optionsSelectedArray = classDetails.optionsSelectedArrayByOptionNameMap.get(section.name);
                const parent = sectionsByName.get(section.parentName || '');
                if ((section.name === subtypes_name) ||
                    (section.name + 's' === subtypes_name) ||
                    (section.name === classDetails.archetype?.name) ||
                    (section.name === 'Ability Score Improvement')) {
                    section.exportSection = false;
                } else if (section.name.toLowerCase() === 'thieves\' cant') {
                    section.exportSection = false;
                } else if (section.name.toLowerCase() === 'Druidic') {
                    section.exportSection = false;
                } else if (section.parentName == null && (section.options.length > 0) && optionsSelectedArray) {
                    // This is a parent of an option set that has a value
                    section.exportSection = false;
                    const optionsSelectedArrayLower = optionsSelectedArray.map(o => o.toLowerCase());
                    for (const child of section.children) {
                        if (optionsSelectedArrayLower.includes(child.name.trim().toLowerCase())) {
                            let desc = child.desc;
                            if (desc.startsWith('>')) {
                                desc = desc.substring(1).trim();
                            }
                            monsterSections.push(new Section(section.name + ': ' + child.name.trim(), desc));
                        }
                    }
                } else if (parent && (parent.options.length > 0) &&
                    !classDetails.optionsSelectedArrayByOptionNameMap.get(parent.name)?.includes(section.name)) {
                    // this is a child of a parent that has an option that is not set to this child.
                    section.exportSection = false;
                } else {
                    // This is fine to export, unless the ClassService.parseSkills method already this as "exportSection = false":
                    if (section.exportSection === undefined || section.exportSection === null) {
                        section.exportSection = true;
                    }
                }
                if (section.exportSection) {
                    monsterSections.push(section);
                    section.children?.forEach(c => {
                        monsterSections.push(c);
                    });
                }
            }
        }
    }

    static classData: Class[] = [];
    static getClassByName(className: string): Class | undefined {
        if (this.classData.length) {
            return this.classData.filter(classData => classData.name.toLocaleLowerCase() == className.toLocaleLowerCase())?.pop();
        }
        ClassService.fetchClasses(false).then((classData: Class[]) => ClassService.classData = classData);
    }

    static async fetchClasses(cacheOnly: boolean, progCallback?: (data: any[], totalCount: number) => void): Promise<Class[]> {
        try {
            const classObjs: any[] = await ApiService.getAllClasses(cacheOnly, progCallback);
            const allClasses: ClassOpen5e[] = [];
            for (const classObj of classObjs) {
                const clss: ClassOpen5e = {
                    name: '',
                    desc: '',
                    slug: '',
                    base: { name: '' },
                    subtypes_name: '',
                    hit_dice: '',
                    prof_armor: '',
                    prof_weapons: '',
                    prof_tools: '',
                    prof_saving_throws: '',
                    prof_skills: '',
                    equipment: '',
                    spellcasting_ability: '',
                    table: '',
                    sections: [],
                    archetypes: [],
                    getAsiLevels: (level: number) => [],
                    document__slug: '', document__title: '', document__license_url: '', document__url: ''
                };
                Object.assign(clss, classObj);
                allClasses.push(clss);
            }

            const classWithArchetypes = ClassService.expandArchetypes(allClasses);

            // Fix source errors:
            classWithArchetypes.forEach(fixClassErrors);
            // Add the getAsiLevels() method to each class:
            classWithArchetypes.forEach((pcClass) => ClassService.addAsiLevelToClass(pcClass));
            classWithArchetypes.forEach((pcClass) => ClassService.parseSections(pcClass));

            // update our cached copy:
            ClassService.classData = classWithArchetypes;
            return classWithArchetypes;
        } catch (error) {
            console.error("Failed to fetch classes", error);
            return [];
        }
    }
}
export default ClassService;